import React, { useState } from "react";
import { ITabReport } from "redux/app/logs/@types";
import classNames from "classnames";
import moment from "moment";

interface IDateTimeChangeFormProps {
    report: ITabReport;
    onClose: () => void;
    onSubmit: (data: any) => void;
}

const DateTimeChangeForm: React.FC<IDateTimeChangeFormProps> = ({
    report,
    onClose,
    onSubmit,
}) => {
    const [selectedReport, setSelectedReport] = useState<number | null>(null);
    const [selectedCargoIndex, setSelectedCargoIndex] = useState<number | null>(null);
    const [selectedBunkerIndex, setSelectedBunkerIndex] = useState<number | null>(null);
    const [fromDate, setFromDate] = useState<string>("");
    const [fromTime, setFromTime] = useState<string>("");
    const [fromTimezone, setFromTimezone] = useState<string>("");
    const [toDate, setToDate] = useState<string>("");
    const [toTime, setToTime] = useState<string>("");
    const [toTimezone, setToTimezone] = useState<string>("");
    const [allFastDate, setAllFastDate] = useState<string>("");
    const [allFastTime, setAllFastTime] = useState<string>("");
    const [allFastTimezone, setAllFastTimezone] = useState<string>("");
    const [castOffDate, setCastOffDate] = useState<string>("");
    const [castOffTime, setCastOffTime] = useState<string>("");
    const [castOffTimezone, setCastOffTimezone] = useState<string>("");
    const [error, setError] = useState<string | null>(null);

    const TIMEZONES = Array.from({ length: 25 }, (_, i) => {
        const offset = i - 12;
        const sign = offset >= 0 ? "+" : "-";
        const hours = Math.abs(offset).toString().padStart(2, "0");
        return `${sign}${hours}:00`;
    });

    // Handle report selection and auto-populate date/time fields
    const handleReportSelect = (index: number) => {
        setSelectedReport(index);
        setSelectedCargoIndex(null);
        setSelectedBunkerIndex(null);
        const selectedReportData = report.timeline[index];

        if (isStandaloneCargoReport(selectedReportData)) {
            // For standalone cargo reports, populate both standard and all fast/cast off fields
            // Main fields are standard from/to (for report timeline)
            setFromDate(selectedReportData.from_local_date || "");
            setFromTime(selectedReportData.from_local_time || "");
            setFromTimezone(selectedReportData.from_zd || "");
            setToDate(selectedReportData.to_local_date || "");
            setToTime(selectedReportData.to_local_time || "");
            setToTimezone(selectedReportData.to_zd || "");
            // Additional fields are all fast and cast off (for cargo operations)
            setAllFastDate(selectedReportData.from_all_fast_local_date || "");
            setAllFastTime(selectedReportData.from_all_fast_local_time || "");
            setAllFastTimezone(selectedReportData.from_all_fast_zd || "");
            setCastOffDate(selectedReportData.to_cast_off_local_date || "");
            setCastOffTime(selectedReportData.to_cast_off_local_time || "");
            setCastOffTimezone(selectedReportData.to_cast_off_zd || "");
        } else if (isStandaloneBunkerReport(selectedReportData)) {
            // For standalone bunker reports, use bunker delivery fields
            setFromDate(selectedReportData.bunker_delivery_local_date || "");
            setFromTime(selectedReportData.bunker_delivery_local_time || "");
            setFromTimezone(selectedReportData.bunker_delivery_zd || "");
            // Bunker reports don't have "to" fields, so we'll use the same values
            setToDate(selectedReportData.bunker_delivery_local_date || "");
            setToTime(selectedReportData.bunker_delivery_local_time || "");
            setToTimezone(selectedReportData.bunker_delivery_zd || "");
            // Clear all fast and cast off fields for bunker reports
            setAllFastDate("");
            setAllFastTime("");
            setAllFastTimezone("");
            setCastOffDate("");
            setCastOffTime("");
            setCastOffTimezone("");
        } else {
            // For other reports, use standard from/to fields
            setFromDate(selectedReportData.from_local_date || "");
            setFromTime(selectedReportData.from_local_time || "");
            setFromTimezone(selectedReportData.from_zd || "");
            setToDate(selectedReportData.to_local_date || "");
            setToTime(selectedReportData.to_local_time || "");
            setToTimezone(selectedReportData.to_zd || "");
            // Clear all fast and cast off fields for non-cargo reports
            setAllFastDate("");
            setAllFastTime("");
            setAllFastTimezone("");
            setCastOffDate("");
            setCastOffTime("");
            setCastOffTimezone("");
        }
    };

    // Handle cargo report selection
    const handleCargoSelect = (cargoIndex: number) => {
        setSelectedCargoIndex(cargoIndex);
        setSelectedBunkerIndex(null);
        const selectedReportData = report.timeline[selectedReport!];
        const cargoReport = selectedReportData.cargoReport[cargoIndex];
        // For embedded cargo reports, main fields are all fast and cast off dates
        setFromDate(cargoReport.from_all_fast_local_date || "");
        setFromTime(cargoReport.from_all_fast_local_time || "");
        setFromTimezone(cargoReport.from_all_fast_zd || "");
        setToDate(cargoReport.to_cast_off_local_date || "");
        setToTime(cargoReport.to_cast_off_local_time || "");
        setToTimezone(cargoReport.to_cast_off_zd || "");
        // Clear all fast and cast off fields for embedded cargo reports
        setAllFastDate("");
        setAllFastTime("");
        setAllFastTimezone("");
        setCastOffDate("");
        setCastOffTime("");
        setCastOffTimezone("");
    };

    // Handle bunker report selection
    const handleBunkerSelect = (bunkerIndex: number) => {
        setSelectedBunkerIndex(bunkerIndex);
        setSelectedCargoIndex(null);
        const bunkerReport = report.standaloneForm?.bunker?.bunkerReport[bunkerIndex];
        setFromDate(bunkerReport.bunker_delivery_local_date || "");
        setFromTime(bunkerReport.bunker_delivery_local_time || "");
        setFromTimezone(bunkerReport.bunker_delivery_zd || "");
        // Bunker reports don't have "to" fields, so we'll use the same values
        setToDate(bunkerReport.bunker_delivery_local_date || "");
        setToTime(bunkerReport.bunker_delivery_local_time || "");
        setToTimezone(bunkerReport.bunker_delivery_zd || "");
        // Clear all fast and cast off fields for bunker reports
        setAllFastDate("");
        setAllFastTime("");
        setAllFastTimezone("");
        setCastOffDate("");
        setCastOffTime("");
        setCastOffTimezone("");
    };


    const validateDateTime = (fromDateTime: moment.Moment, toDateTime: moment.Moment): boolean => {
        // For bunker reports, we don't need timeline validation since they're standalone
        if (selectedBunkerIndex !== null) {
            // Only validate that toDateTime is not before fromDateTime (which should be the same for bunker reports)
            if (toDateTime.isBefore(fromDateTime)) {
                setError("Bunker delivery date/time cannot be invalid");
                return false;
            }
            return true;
        }

        // For timeline reports, proceed with normal validation
        const timeline = report.timeline || [];
        const currentReport = timeline[selectedReport!];

        // If we're editing a cargo report, validate against its parent departure report
        if (selectedCargoIndex !== null) {
            const departureReport = currentReport;
            const departureToDateTime = moment(`${departureReport.to_local_date} ${departureReport.to_local_time}`, "YYYY-MM-DD HH:mm");

            // Cargo report's all fast and cast off dates should not exceed departure report's end date
            if (fromDateTime.isAfter(departureToDateTime)) {
                setError("Cargo report's all fast date/time cannot be after the departure report's end date/time");
                return false;
            }
            if (toDateTime.isAfter(departureToDateTime)) {
                setError("Cargo report's cast off date/time cannot be after the departure report's end date/time");
                return false;
            }
        } else if (isStandaloneCargoReport(currentReport)) {
            // For standalone cargo reports, validate both standard from/to and all fast/cast off dates
            const prevReport = timeline[selectedReport! - 1];
            const nextReport = timeline[selectedReport! + 1];

            // Validate standard from/to dates (main report timeline dates)
            if (prevReport) {
                const prevDateTime = moment(`${prevReport.to_local_date} ${prevReport.to_local_time}`, "YYYY-MM-DD HH:mm");
                if (fromDateTime.isBefore(prevDateTime)) {
                    setError(`From date/time cannot be before the previous report (${prevReport.report_type})`);
                    return false;
                }
            }

            if (nextReport) {
                const nextDateTime = moment(`${nextReport.from_local_date} ${nextReport.from_local_time}`, "YYYY-MM-DD HH:mm");
                if (toDateTime.isAfter(nextDateTime)) {
                    setError(`To date/time cannot be after the next report (${nextReport.report_type})`);
                    return false;
                }
            }

            // Validate all fast/cast off dates (additional cargo operation dates)
            if (allFastDate && allFastTime && castOffDate && castOffTime) {
                const allFastDateTime = moment(`${allFastDate} ${allFastTime}`, "YYYY-MM-DD HH:mm");
                const castOffDateTime = moment(`${castOffDate} ${castOffTime}`, "YYYY-MM-DD HH:mm");

                // Ensure cast off datetime is not before all fast datetime
                if (castOffDateTime.isBefore(allFastDateTime)) {
                    setError("Cast Off date/time cannot be before All Fast date/time");
                    return false;
                }
            }
        } else if (!isStandaloneBunkerReport(currentReport)) {
            // For regular reports, check against adjacent reports
            const prevReport = timeline[selectedReport! - 1];
            const nextReport = timeline[selectedReport! + 1];

            if (prevReport) {
                const prevDateTime = moment(`${prevReport.to_local_date} ${prevReport.to_local_time}`, "YYYY-MM-DD HH:mm");
                if (fromDateTime.isBefore(prevDateTime)) {
                    setError(`From date/time cannot be before the previous report (${prevReport.report_type})`);
                    return false;
                }
            }

            if (nextReport) {
                const nextDateTime = moment(`${nextReport.from_local_date} ${nextReport.from_local_time}`, "YYYY-MM-DD HH:mm");
                if (toDateTime.isAfter(nextDateTime)) {
                    setError(`To date/time cannot be after the next report (${nextReport.report_type})`);
                    return false;
                }
            }
        }

        // Ensure "to" datetime is not before "from" datetime
        if (toDateTime.isBefore(fromDateTime)) {
            setError("Cast off date/time cannot be before All Fast date/time");
            return false;
        }

        return true;
    };

    const handleSubmit = () => {
        if (selectedReport === null && selectedBunkerIndex === null) {
            setError("Please select a report");
            return;
        }

        // For bunker reports, we only need fromDate, fromTime, and fromTimezone
        if (selectedBunkerIndex !== null) {
            if (!fromDate || !fromTime || !fromTimezone) {
                setError("Please fill in all date, time, and timezone fields");
                return;
            }
        } else if (selectedCargoIndex !== null) {
            // For embedded cargo reports, we need all fast and cast off fields
            if (!fromDate || !fromTime || !fromTimezone || !toDate || !toTime || !toTimezone) {
                setError("Please fill in all date, time, and timezone fields");
                return;
            }
        } else if (selectedReport !== null && isStandaloneCargoReport(report.timeline[selectedReport])) {
            // For standalone cargo reports, we need both standard and all fast/cast off fields
            if (!fromDate || !fromTime || !fromTimezone || !toDate || !toTime || !toTimezone ||
                !allFastDate || !allFastTime || !allFastTimezone || !castOffDate || !castOffTime || !castOffTimezone) {
                setError("Please fill in all date, time, and timezone fields");
                return;
            }
        } else {
            // For other reports, we need all fields
            if (!fromDate || !fromTime || !fromTimezone || !toDate || !toTime || !toTimezone) {
                setError("Please fill in all date, time, and timezone fields");
                return;
            }
        }

        const fromDateTime = moment(`${fromDate} ${fromTime}`, "YYYY-MM-DD HH:mm");
        const toDateTime = selectedBunkerIndex !== null ? fromDateTime : moment(`${toDate} ${toTime}`, "YYYY-MM-DD HH:mm");

        if (!validateDateTime(fromDateTime, toDateTime)) {
            return;
        }

        let selectedReportData = null;
        let isStandaloneCargo = false;
        let isStandaloneBunker = false;

        if (selectedBunkerIndex !== null) {
            selectedReportData = report.standaloneForm?.bunker?.bunkerReport[selectedBunkerIndex];
            isStandaloneBunker = true;
        } else if (selectedReport !== null) {
            selectedReportData = report.timeline[selectedReport];
            isStandaloneCargo = isStandaloneCargoReport(selectedReportData);
        }

        // Create backup before date/time change
        const backup = {
            ...report,
            modelName: "backup",
            backupNote: `Changed date/time for ${selectedCargoIndex !== null ? 'cargo report in ' : ''}${selectedBunkerIndex !== null ? 'standalone bunker report' : isStandaloneCargo ? 'standalone cargo report' : selectedReportData?.report_type}`,
            backupDate: moment().toISOString()
        };

        onSubmit({
            reportIndex: selectedReport,
            cargoIndex: selectedCargoIndex,
            bunkerIndex: selectedBunkerIndex,
            fromDate,
            fromTime,
            fromTimezone,
            toDate: selectedBunkerIndex !== null ? fromDate : toDate,
            toTime: selectedBunkerIndex !== null ? fromTime : toTime,
            toTimezone: selectedBunkerIndex !== null ? fromTimezone : toTimezone,
            // Add all fast and cast off fields for standalone cargo reports
            allFastDate: isStandaloneCargo ? allFastDate : undefined,
            allFastTime: isStandaloneCargo ? allFastTime : undefined,
            allFastTimezone: isStandaloneCargo ? allFastTimezone : undefined,
            castOffDate: isStandaloneCargo ? castOffDate : undefined,
            castOffTime: isStandaloneCargo ? castOffTime : undefined,
            castOffTimezone: isStandaloneCargo ? castOffTimezone : undefined,
            isStandaloneCargo,
            isStandaloneBunker,
            backup
        });
    };

    // Check if a report is a departure report with cargo reports
    const isDepartureWithCargo = (report: any) => {
        return report.report_type === "DEPARTURE" && 
               report.isLatest === true && 
               Array.isArray(report.cargoReport) && 
               report.cargoReport.length > 0;
    };

    // Check if a report is a standalone cargo report
    const isStandaloneCargoReport = (report: any) => {
        return report.report_type === "CARGO" && 
               !report._isExtractedFromDeparture && 
               !report.isEmbeddedCargoReport;
    };

    // Check if a report is a standalone bunker report
    const isStandaloneBunkerReport = (report: any) => {
        return report.report_type === "BUNKER" && 
               report._isFromStandaloneForm === true;
    };

    // Check if standalone bunker reports exist
    const hasStandaloneBunkerReports = () => {
        return report.standaloneForm?.bunker?.bunkerReport && 
               Array.isArray(report.standaloneForm.bunker.bunkerReport) && 
               report.standaloneForm.bunker.bunkerReport.length > 0;
    };

    return (
        <div className="p-4">
            <h3 className="mb-4 text-lg font-medium text-gray-900">
                Change Date/Time
            </h3>

            {/* Report Selection */}
            <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                    Select Report
                </label>
                <select
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                    value={selectedReport ?? ""}
                    onChange={(e) => handleReportSelect(Number(e.target.value))}
                >
                    <option value="">Select a report...</option>
                    {(report.timeline || []).map((item, index) => (
                        <option key={index} value={index}>
                            {item.report_type} ({item.from_local_date} {item.from_local_time} {item.from_zd} to {item.to_local_date} {item.to_local_time} {item.to_zd})
                        </option>
                    ))}
                </select>
            </div>

            {/* Standalone Bunker Report Selection */}
            {hasStandaloneBunkerReports() && (
                <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                        Select Standalone Bunker Report
                    </label>
                    <select
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                        value={selectedBunkerIndex ?? ""}
                        onChange={(e) => handleBunkerSelect(Number(e.target.value))}
                    >
                        <option value="">Select a bunker report...</option>
                        {report.standaloneForm.bunker.bunkerReport.map((bunker: any, index: number) => (
                            <option key={index} value={index}>
                                Bunker Report {index + 1} ({bunker.bunker_delivery_local_date} {bunker.bunker_delivery_local_time} {bunker.bunker_delivery_zd})
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* Cargo Report Selection (if applicable) */}
            {selectedReport !== null && isDepartureWithCargo(report.timeline[selectedReport]) && (
                <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                        Select Cargo Report
                    </label>
                    <select
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                        value={selectedCargoIndex ?? ""}
                        onChange={(e) => handleCargoSelect(Number(e.target.value))}
                    >
                        <option value="">Select a cargo report...</option>
                        {report.timeline[selectedReport].cargoReport.map((cargo: any, index: number) => (
                            <option key={index} value={index}>
                                Cargo Report {index + 1} ({cargo.from_all_fast_local_date} {cargo.from_all_fast_local_time} {cargo.from_all_fast_zd} to {cargo.to_cast_off_local_date} {cargo.to_cast_off_local_time} {cargo.to_cast_off_zd})
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* From Date/Time/Timezone Selection */}
            <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                    {selectedCargoIndex !== null ? 'All Fast' : selectedBunkerIndex !== null ? 'Bunker Delivery' : 'From'} Date/Time/Timezone
                </label>
                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <input
                            type="date"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                    </div>
                    <div>
                        <input
                            type="time"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                            value={fromTime}
                            onChange={(e) => setFromTime(e.target.value)}
                        />
                    </div>
                    <div>
                        <select
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                            value={fromTimezone}
                            onChange={(e) => setFromTimezone(e.target.value)}
                        >
                            <option value="">Select timezone...</option>
                            {TIMEZONES.map((tz) => (
                                <option key={tz} value={tz}>
                                    UTC {tz}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* To Date/Time/Timezone Selection - Only show for non-bunker reports */}
            {selectedBunkerIndex === null && (
                <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                        {selectedCargoIndex !== null ? 'Cast Off' : 'To'} Date/Time/Timezone
                    </label>
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <input
                                type="date"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </div>
                        <div>
                            <input
                                type="time"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                                value={toTime}
                                onChange={(e) => setToTime(e.target.value)}
                            />
                        </div>
                        <div>
                            <select
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                                value={toTimezone}
                                onChange={(e) => setToTimezone(e.target.value)}
                            >
                                <option value="">Select timezone...</option>
                                {TIMEZONES.map((tz) => (
                                    <option key={tz} value={tz}>
                                        UTC {tz}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* All Fast/Cast Off Date/Time/Timezone Selection - Only show for standalone cargo reports */}
            {selectedReport !== null && isStandaloneCargoReport(report.timeline[selectedReport]) && (
                <>
                    <div className="mb-4">
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                            All Fast Date/Time/Timezone
                        </label>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <input
                                    type="date"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                                    value={allFastDate}
                                    onChange={(e) => setAllFastDate(e.target.value)}
                                />
                            </div>
                            <div>
                                <input
                                    type="time"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                                    value={allFastTime}
                                    onChange={(e) => setAllFastTime(e.target.value)}
                                />
                            </div>
                            <div>
                                <select
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                                    value={allFastTimezone}
                                    onChange={(e) => setAllFastTimezone(e.target.value)}
                                >
                                    <option value="">Select timezone...</option>
                                    {TIMEZONES.map((tz) => (
                                        <option key={tz} value={tz}>
                                            UTC {tz}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                            Cast Off Date/Time/Timezone
                        </label>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <input
                                    type="date"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                                    value={castOffDate}
                                    onChange={(e) => setCastOffDate(e.target.value)}
                                />
                            </div>
                            <div>
                                <input
                                    type="time"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                                    value={castOffTime}
                                    onChange={(e) => setCastOffTime(e.target.value)}
                                />
                            </div>
                            <div>
                                <select
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                                    value={castOffTimezone}
                                    onChange={(e) => setCastOffTimezone(e.target.value)}
                                >
                                    <option value="">Select timezone...</option>
                                    {TIMEZONES.map((tz) => (
                                        <option key={tz} value={tz}>
                                            UTC {tz}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {error && (
                <div className="p-3 mb-4 text-sm text-red-700 bg-red-100 rounded-md">
                    {error}
                </div>
            )}

            {/* Warning Message */}
            {(selectedReport !== null || selectedBunkerIndex !== null) && fromDate && fromTime && fromTimezone && (selectedBunkerIndex !== null || (toDate && toTime && toTimezone)) && (
                <div className="p-3 mb-4 text-sm text-yellow-700 bg-yellow-100 rounded-md">
                    Warning: This will change the {selectedCargoIndex !== null ? 'All Fast and Cast Off' : selectedBunkerIndex !== null ? 'Bunker Delivery' : selectedReport !== null && isStandaloneCargoReport(report.timeline[selectedReport]) ? 'from, to, All Fast, and Cast Off' : 'from and to'} date/time/timezone of the {selectedCargoIndex !== null ? 'selected cargo report' : selectedBunkerIndex !== null ? 'selected bunker report' : 'selected report'}.
                    Please ensure this change maintains the chronological sequence of events.
                    A backup will be created before the change.
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
                <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={
                        (selectedReport === null && selectedBunkerIndex === null) || 
                        !fromDate || !fromTime || !fromTimezone || 
                        (selectedBunkerIndex === null && (!toDate || !toTime || !toTimezone)) ||
                        (selectedReport !== null && isStandaloneCargoReport(report.timeline[selectedReport]) && 
                         (!allFastDate || !allFastTime || !allFastTimezone || !castOffDate || !castOffTime || !castOffTimezone))
                    }
                    className={classNames(
                        "px-4 py-2 text-sm font-medium text-white rounded-md",
                        {
                            "bg-blue-600 hover:bg-blue-700": (selectedReport !== null || selectedBunkerIndex !== null) && 
                                fromDate && fromTime && fromTimezone && 
                                (selectedBunkerIndex !== null || (toDate && toTime && toTimezone)) &&
                                !(selectedReport !== null && isStandaloneCargoReport(report.timeline[selectedReport]) && 
                                  (!allFastDate || !allFastTime || !allFastTimezone || !castOffDate || !castOffTime || !castOffTimezone)),
                            "bg-gray-300 cursor-not-allowed": (selectedReport === null && selectedBunkerIndex === null) || 
                                !fromDate || !fromTime || !fromTimezone || 
                                (selectedBunkerIndex === null && (!toDate || !toTime || !toTimezone)) ||
                                (selectedReport !== null && isStandaloneCargoReport(report.timeline[selectedReport]) && 
                                 (!allFastDate || !allFastTime || !allFastTimezone || !castOffDate || !castOffTime || !castOffTimezone)),
                        }
                    )}
                >
                    Change Date/Time
                </button>
            </div>
        </div>
    );
};

export default DateTimeChangeForm; 